const defaultMeasurementId = 'G-PCD7LYHBY2';
const campaignKeys = ['utm_source','utm_medium','utm_campaign','utm_term','utm_content','gclid','gbraid','wbraid'] as const;

export interface EnrollmentTrackingContext {
  analyticsConsent:boolean;
  marketingConsent:boolean;
  campaign:Record<string,string>;
  landingPage:string;
  pagePath:string;
  locale:string;
  industry:string;
  offering:string;
}

interface EnrollmentConversion {
  conversionEventId:string;
  planId:string;
  industry:string;
  mode:string;
  billingFrequency:string;
  amountMinor?:number;
}

declare global {
  interface Window {
    dataLayer?:unknown[];
    gtag?:(...args:unknown[])=>void;
  }
}

function clean(value:string|null,max=200) {
  const result=(value??'').trim();
  return result.length<=max&&/^[\w .,%+\-:@/]*$/u.test(result)?result:'';
}

export function readEnrollmentTrackingContext(search:string):EnrollmentTrackingContext {
  const query=new URLSearchParams(search);
  const campaign:Record<string,string>={};
  for(const key of campaignKeys){const value=clean(query.get(key));if(value)campaign[key]=value;}
  return {
    analyticsConsent:query.get('epos_analytics_consent')==='granted',
    marketingConsent:query.get('epos_marketing_consent')==='granted',
    campaign,
    landingPage:clean(query.get('epos_landing_page')),
    pagePath:clean(query.get('epos_page_path')),
    locale:clean(query.get('epos_locale'),35),
    industry:clean(query.get('industry'),100),
    offering:clean(query.get('offering'),100)
  };
}

export function enrollmentAttribution(context:EnrollmentTrackingContext) {
  if(!context.marketingConsent)return {};
  return {
    marketingConsent:true,
    analyticsConsent:context.analyticsConsent,
    marketingAttribution:{...context.campaign,landingPage:context.landingPage,pagePath:context.pagePath,locale:context.locale,industry:context.industry,offering:context.offering}
  };
}

function measurementId(){
  const configured=(process.env.REACT_APP_EPOS_BOOKINGS_GA4_MEASUREMENT_ID||defaultMeasurementId).trim();
  return /^G-[A-Z0-9]{6,20}$/.test(configured)?configured:'';
}

function ensureGtag(){
  window.dataLayer=window.dataLayer||[];
  window.gtag=window.gtag||function(...args:unknown[]){window.dataLayer!.push(args);};
  return window.gtag;
}

export function initialiseEnrollmentMeasurement(context:EnrollmentTrackingContext){
  const gtag=ensureGtag();
  gtag('consent','default',{analytics_storage:'denied',ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied',functionality_storage:'granted',security_storage:'granted'});
  gtag('consent','update',{analytics_storage:context.analyticsConsent?'granted':'denied',ad_storage:context.marketingConsent?'granted':'denied',ad_user_data:context.marketingConsent?'granted':'denied',ad_personalization:context.marketingConsent?'granted':'denied'});
  const id=measurementId();
  if(!id||(!context.analyticsConsent&&!context.marketingConsent))return;
  gtag('config',id,{send_page_view:true,linker:{domains:['eposbookings.com','bookings.epos-hub.co.uk'],accept_incoming:true}});
  if(!document.getElementById('epos-bookings-google-tag')){
    const script=document.createElement('script');script.id='epos-bookings-google-tag';script.async=true;script.src=`https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;document.head.appendChild(script);
  }
}

export function trackEnrollmentConversion(context:EnrollmentTrackingContext,conversion:EnrollmentConversion){
  if(!context.analyticsConsent)return;
  const id=measurementId();if(!id)return;
  const gtag=ensureGtag();
  const common={send_to:id,event_id:conversion.conversionEventId,form_source:'website_enrolment',plan_id:conversion.planId,industry:conversion.industry||context.industry,enrolment_mode:conversion.mode,billing_frequency:conversion.billingFrequency,...context.campaign};
  gtag('event','sign_up',{...common,method:'website_enrolment'});
  gtag('event','generate_lead',{...common,currency:'GBP',value:conversion.amountMinor===undefined?undefined:conversion.amountMinor/100});
  window.dataLayer!.push({event:'eposbookings_enrolment_completed',...common});
}
